import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from 'modules/Finance/PaymentProfiles/List/payment-profiles.module.scss'
import List from './subcomponents/List'
import FilterPanel from 'components/Filters/FilterPanel'
import config from 'modules/Finance/PaymentProfiles/List/filters'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import PageHeader from 'components/PageHeader'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  DROP_PAYMENT_PROFILES,
  GET_PAYMENT_PROFILES,
  PAYMENT_PROFILES_SET_FILTER,
} from 'modules/Finance/PaymentProfiles/types'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import GridSpinner from 'components/UI/GridSpinner'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as NoDataFound } from 'images/illustration/no-data-found.svg'
import { Button, Grid, Icon } from 'gu-ui-kit'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'

const PaymentProfiles = () => {
  const [page, setPage] = useState(1)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { sm } = useBreakpoint()
  const pathGen = useGeneratePathI18n()

  const {
    payment_profiles: { data, loading, pagination, loaded },
  } = useSelector((state) => ({
    payment_profiles: state['finance/payment-profiles'],
    i18n: state['app'].i18n,
  }))

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['finance/payment-profiles'].filters),
    config: config(),
    setAction: PAYMENT_PROFILES_SET_FILTER,
    localStorageKey: 'payment-profiles',
  })

  useEffect(() => {
    filterManager.params &&
      dispatch({
        type: GET_PAYMENT_PROFILES,
        payload: {
          params: {
            ...filterManager.params,
            offset: (page - 1) * pagination.limit,
            limit: pagination.limit,
          },
        },
      })
  }, [filterManager.params, page])

  useEffect(() => {
    dispatch({
      type: DROP_PAYMENT_PROFILES,
    })

    setPage(1)
  }, [filterManager.params])

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(filterManager.params, data, loaded)

  return (
    <div className={styles['container']}>
      <PageHeader className={'margin-bottom-24'}>{t('payment_profiles::header')}</PageHeader>
      {firstLoad ? (
        <GridSpinner width={32} />
      ) : totallyEmptyList ? (
        <EmptyResults
          image={<NoDataFound />}
          text={t('payment_profiles::empty_list.text')}
          actionButton={
            <Button
              startIcon={<Icon name={'plus'} />}
              variant={'primary'}
              component={Link}
              to={pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW)}
            >
              {t('payment_profiles::add_button')}
            </Button>
          }
        />
      ) : (
        <>
          <Grid
            direction={'row'}
            className={'margin-bottom-64'}
          >
            <Button
              startIcon={<Icon name={'plus'} />}
              variant={'primary'}
              fullWidth={sm}
              component={Link}
              to={pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW)}
              size={sm ? 'm' : 'l'}
            >
              {t('payment_profiles::add_button')}
            </Button>
          </Grid>
          <FilterPanel
            className={styles['filter-panel']}
            filterManager={filterManager}
          />
          <List
            EmptyComponent={() => (
              <EmptyResults
                image={<NoDataFound />}
                header={t('payment_profiles::data_list.empty.heading')}
                text={t('payment_profiles::data_list.empty.text')}
              />
            )}
            loading={loading}
            data={data}
            pagination={pagination}
            onChangePage={(val) => setPage(page + val)}
          />
        </>
      )}
    </div>
  )
}

export default PaymentProfiles
